import { useState } from "react";
import { toast } from "sonner";
import {
  Bell, X, Loader2, UserPlus, Inbox, ShieldCheck, FileUp, MessageSquare, DollarSign, CheckCheck, Heart,
} from "lucide-react";
import {
  dismissNotification, dismissAllNotifications, readAllNotifications, formatApiError,
} from "@/lib/api";

const ICONS = {
  signup: UserPlus,
  enquiry: Inbox,
  details: ShieldCheck,
  document: FileUp,
  message: MessageSquare,
  payment: DollarSign,
  satisfaction: Heart,
};

const timeAgo = (iso) => {
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
};

export const NotificationBell = ({ adminKey, data, onRefresh, onOpen }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const items = data?.notifications || [];
  const unread = data?.unread || 0;

  const act = async (fn, id = "all") => {
    setBusy(id);
    try {
      await fn();
      await onRefresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="relative">
      <button
        type="button"
        data-testid="notif-bell"
        onClick={() => {
          setOpen((o) => !o);
          if (!open && unread > 0) act(() => readAllNotifications(adminKey));
        }}
        className="relative flex items-center gap-2 border border-steel-border px-4 py-2.5 text-sm font-medium text-steel-secondary transition-colors duration-300 hover:border-steel-brass hover:text-steel-brass"
      >
        <Bell className="h-4 w-4" />
        {items.length > 0 && (
          <span
            data-testid="notif-count"
            className={`min-w-5 border px-1.5 py-0.5 font-mono text-[10px] leading-none ${
              unread > 0 ? "border-steel-brass bg-steel-brass/20 text-steel-brass" : "border-steel-border text-steel-muted"
            }`}
          >
            {items.length}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notif-panel"
          data-lenis-prevent
          onWheel={(e) => e.stopPropagation()}
          className="absolute right-0 z-50 mt-2 w-[26rem] max-w-[92vw] border border-steel-border bg-steel-surface shadow-[0_24px_70px_rgba(0,0,0,0.6)]"
        >
          <div className="flex items-center justify-between border-b border-steel-border px-5 py-3">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-steel-muted">
              Queue · {items.length} to action
            </p>
            <div className="flex items-center gap-3">
              {items.length > 0 && (
                <button
                  type="button"
                  data-testid="notif-dismiss-all"
                  onClick={() => act(() => dismissAllNotifications(adminKey))}
                  disabled={busy === "all"}
                  className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-steel-muted transition-colors hover:text-steel-brass disabled:opacity-50"
                >
                  {busy === "all" ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCheck className="h-3 w-3" />}
                  Clear all
                </button>
              )}
              <button type="button" data-testid="notif-close" onClick={() => setOpen(false)} className="text-steel-muted transition-colors hover:text-steel-text">
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>

          <div className="max-h-[26rem] overflow-y-auto overscroll-contain">
            {items.length === 0 ? (
              <p className="px-5 py-8 text-center text-sm text-steel-muted" data-testid="notif-empty">
                Nothing waiting. You're all caught up.
              </p>
            ) : (
              items.map((n) => {
                const Icon = ICONS[n.kind] || Bell;
                return (
                  <div
                    key={n.id}
                    data-testid={`notif-item-${n.id}`}
                    className={`flex items-start gap-3 border-b border-steel-border/60 px-5 py-4 transition-colors ${
                      n.read_at ? "" : "bg-steel-brass/5"
                    }`}
                  >
                    <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center border border-steel-border">
                      <Icon className="h-3.5 w-3.5 text-steel-brass" />
                    </div>
                    <button
                      type="button"
                      data-testid={`notif-open-${n.id}`}
                      onClick={() => {
                        setOpen(false);
                        onOpen(n);
                      }}
                      className="flex-1 text-left"
                    >
                      <p className="text-sm font-medium leading-snug text-steel-text">{n.title}</p>
                      {n.body && <p className="mt-1 text-xs leading-relaxed text-steel-secondary">{n.body}</p>}
                      <p className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-steel-muted">
                        {timeAgo(n.created_at)} · open {n.enquiry_id ? "enquiry" : n.tab === "invoices" ? "invoices" : "client"}
                      </p>
                    </button>
                    <button
                      type="button"
                      data-testid={`notif-dismiss-${n.id}`}
                      onClick={() => act(() => dismissNotification(adminKey, n.id), n.id)}
                      disabled={busy === n.id}
                      title="Dismiss"
                      className="mt-0.5 shrink-0 text-steel-muted transition-colors hover:text-red-400 disabled:opacity-50"
                    >
                      {busy === n.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5" />}
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
